import {
  Button,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Grid,
  GridItem,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { Spacer } from 'src/components/__legacy__/spacer/spacer'
import { MemberLabel } from 'src/components/MemberLabel/MemberLabel'
import { formatDate } from 'src/utils/formatDate'

import { Select } from '../../../../../../../../components/Select/Select'
import {
  IcfDocumentCheckRole,
  IcfDocumentDetail,
  IcfDocumentRevision,
  IcfDocumentSignerRole,
  icfDocumentSignFieldTypes,
  icfDocumentSignFieldTypeText,
  icfDocumentSignFieldTypeToRole,
  icfDocumentTypes,
  icfDocumentTypeText,
  icfRevActionText,
} from '../entity'

type Props = {
  icfDocument: IcfDocumentDetail | undefined
  selectedRevision: IcfDocumentRevision | undefined
  selectedRevisionUid: string | undefined
  onChangeSelectedRevisionUid: (uid: string) => void
  filePreviewUrl: string
  onRedirectDocuSignPreview: ({
    dsTemplateId,
    role,
  }: {
    dsTemplateId: string
    role: IcfDocumentSignerRole | IcfDocumentCheckRole
  }) => void
}

export const RevisionHistoryRenewal: React.FC<Props> = ({
  icfDocument,
  selectedRevision,
  selectedRevisionUid,
  onChangeSelectedRevisionUid,
  filePreviewUrl,
  onRedirectDocuSignPreview,
}) => {
  if (!icfDocument || !selectedRevisionUid) {
    return null
  }

  return (
    <>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <Spacer size={16} />
        <DrawerBody>
          <Flex align="start" pr="6">
            <Grid
              flex="1"
              templateColumns="max-content 1fr"
              gap="6"
              alignItems="center"
            >
              <GridItem>
                <Text fontWeight="medium" fontSize="sm" color="gray.600">
                  文書ID
                </Text>
              </GridItem>
              <GridItem>
                <Text>{icfDocument.numberingId}</Text>
              </GridItem>

              <GridItem>
                <Text fontWeight="medium" fontSize="sm" color="gray.600">
                  文書名
                </Text>
              </GridItem>
              <GridItem>
                <Text>{selectedRevision?.name}</Text>
              </GridItem>
              <GridItem>
                <Text fontWeight="medium" fontSize="sm" color="gray.600">
                  文書の種類
                </Text>
              </GridItem>
              <GridItem>
                <Text>
                  {selectedRevision &&
                    icfDocumentTypeText[selectedRevision.type]}
                </Text>
              </GridItem>
              {selectedRevision &&
                selectedRevision.type === icfDocumentTypes.AgreementForm && (
                  <>
                    <GridItem>
                      <Text fontWeight="medium" fontSize="sm" color="gray.600">
                        患者の同意
                      </Text>
                    </GridItem>
                    <GridItem>
                      {/* TODO: isRequiredPatientSignがfalseの場合の表示を確認 */}
                      <Text>
                        {selectedRevision?.isRequiredPatientSign
                          ? '必須'
                          : '必須ではない'}
                      </Text>
                    </GridItem>
                  </>
                )}
              <GridItem>
                <Text fontWeight="medium" fontSize="sm" color="gray.600">
                  ファイル名
                </Text>
              </GridItem>
              <GridItem>
                <Button
                  as="a"
                  href={filePreviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="link"
                  minW="auto"
                >
                  <Text>{selectedRevision?.fileName}</Text>
                </Button>
              </GridItem>
              {selectedRevision?.type === icfDocumentTypes.AgreementForm && (
                <>
                  <GridItem>
                    <Text fontWeight="medium" fontSize="sm" color="gray.600">
                      署名ロール
                    </Text>
                  </GridItem>
                  <GridItem>
                    <HStack>
                      {icfDocumentSignFieldTypes.map(sfType => (
                        <Button
                          minW="auto"
                          key={sfType}
                          variant="link"
                          alignItems="left"
                          isDisabled={
                            !selectedRevision.signFields?.some(
                              signField => signField.signFieldType === sfType,
                            )
                          }
                          onClick={() =>
                            onRedirectDocuSignPreview({
                              dsTemplateId: selectedRevision.dsTemplateId,
                              role: icfDocumentSignFieldTypeToRole[sfType],
                            })
                          }
                        >
                          {icfDocumentSignFieldTypeText[sfType]}
                        </Button>
                      ))}
                    </HStack>
                  </GridItem>
                </>
              )}
            </Grid>

            <HStack>
              <Text fontWeight="medium" fontSize="sm" color="gray.600">
                版数
              </Text>
              <Select
                width={100}
                value={selectedRevisionUid}
                items={icfDocument.revisions.map(revision => ({
                  label: revision.version,
                  value: revision.uid,
                }))}
                onChange={onChangeSelectedRevisionUid}
              />
            </HStack>
          </Flex>

          <Text as="h3" mt="12" fontWeight="bold">
            変更履歴
          </Text>
          <TableContainer mt="2">
            <Table>
              <Thead>
                <Tr>
                  <Th w="20%">操作</Th>
                  <Th w="30%">操作日時</Th>
                  <Th w="20%">操作者</Th>
                  <Th w="30%">コメント</Th>
                </Tr>
              </Thead>
              <Tbody>
                {selectedRevision?.histories
                  .sort(
                    (a, b) =>
                      new Date(b.operatedAt).getTime() -
                      new Date(a.operatedAt).getTime(),
                  )
                  .map(history => (
                    <Tr key={history.uid}>
                      <Td>{icfRevActionText[history.action]}</Td>
                      <Td>
                        {formatDate(
                          history.operatedAt,
                          'YYYY/MM/DD (ddd) HH:mm',
                        )}
                      </Td>
                      <Td maxW="200px">
                        <MemberLabel
                          role={history.operatedMemberRole}
                          displayName={history.operatedMemberName}
                        />
                      </Td>
                      <Td>
                        <Text>{history.comment}</Text>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </TableContainer>
        </DrawerBody>
      </DrawerContent>
    </>
  )
}
